import { push, replace } from 'connected-react-router';
import * as inlineManual from 'integrations/inlineManual';
import session from 'lib/session';
import { put, select, takeEvery } from 'redux-saga/effects';
import { error, success } from 'redux/shelf';
import { createSelector } from 'reselect';
import {
  getFirstMerchant,
  getMerchant,
  getMerchantApiKey,
  getMerchantById,
} from './selectors/merchant';
import { getUser } from './selectors/user';

import * as analytics from 'integrations/analytics';
import { actions as notifications } from 'modules/notifications';
import actions from 'modules/user/actions';

import * as config from 'config';

const getSearch = (state) => state.router.location.search;

const redirectToOnboarding = () =>
  (document.location.href = `${config.onboardUrl}/auth/${session.token.replace(
    /\./g,
    '&&'
  )}`);

const getRouteMerchantId = createSelector([getSearch], (search = '') => {
  const match = search.match(/\?merchantId=(.*)/);
  return match && match[1];
});

function* watch403errors() {
  yield takeEvery('*', function* (action) {
    if (action && action.payload && action.payload.status === 403) {
      yield redirectToSignInPage();
    }
  });
}

/**
 * Commented out. Not needed anymore as we are bound to Stripe and none is configured within MD currently.
 */
/*function* watchCC() {
  yield takeEvery(success(actions.validateCC), function* (action) {
    if (action && action.payload && action.payload.isCCExpired) {
      yield put(
        notifications.create({
          text: 'Your payment card will expire soon or has expired recently. Please update this information urgently to avoid any service interruption.',
          type: 'warning',
          timeout: 10e10,
          button: {
            title: 'take me there',
            onClick: () => history.push('/account/billing'),
          },
        })
      );
    }
  });
}*/

function* redirectToSignInPage() {
  session.reset();
  yield put(push('/auth/sign-in'));
}

function* watchKcSignOut() {
  yield takeEvery(actions.kcSignOut, redirectToSignInPage);
}

function* watchSignOut() {
  yield takeEvery(actions.signOut, redirectToSignInPage);
}

function* watchUserDataFetchFailure() {
  yield takeEvery(error(actions.getUser), redirectToSignInPage);
}

function* watchUserDataFetchSuccess() {
  yield takeEvery(actions.getUser, function* () {
    const routeId = yield select(getRouteMerchantId);
    if (!routeId) {
      const firstMerchant = yield select(getFirstMerchant);
      const selectedMerchant = yield select(
        getMerchantById(Number(session.merchant))
      );
      const merchant = !!session.merchant ? selectedMerchant : firstMerchant;
      if (!merchant.merchantID) {
        // Not onboarded yet.

        put(push(`onboarding?onboardingId=${merchant.onboardingID}`));
        return;
      }
      session.merchant = Number(merchant.merchantID);
      yield put(actions.setMerchantId(Number(merchant.merchantID)));
      if (!merchant.onboardingDone) {
        put(push('/setup/product-sync/overview'));
        return;
      }
      return;
    }
    if (!!Number(routeId)) {
      session.merchant = Number(routeId);
      yield put(actions.setMerchantId(Number(routeId)));
    }
  });
}

function* watchMerchantChange() {
  yield takeEvery(actions.getMerchant, function* () {
    const merchant = yield select(getMerchant);
    const user = yield select(getUser);
    session.key = yield select(getMerchantApiKey);
    session.tier = merchant.tierId;
    if (!__DEVELOPMENT__) inlineManual.init(user);
    analytics.init(user, merchant);

    if (
      merchant.platform === 'shopify' &&
      merchant.shopifyLiquid &&
      !merchant.planConfirmed
    ) {
      yield put(push('/onboard-shopify'));
      return;
    }
    if (!merchant.onboardingDone) {
      yield put(push('/setup/product-sync/overview'));
      return;
    }
  });
}

const getPlanhatAction = (path) => {
  let splice = path.split('/');
  const data: any = {};
  splice.shift();
  if (!isNaN(splice[splice.length - 1])) {
    data.id = splice[splice.length - 1];
    splice.pop();
  }
  if (
    splice.includes('pinning') &&
    !['pinning', 'create', 'edit'].includes(splice[splice.length - 1])
  ) {
    data.id = splice[splice.length - 1];
    splice.pop();
  }
  if (
    splice.includes('blacklisting') &&
    !['products', 'suggestions'].includes(splice[splice.length - 1])
  ) {
    data.id = splice[splice.length - 1];
    splice.pop();
  }
  return [splice.join(':') || 'home', data];
};

function* watchRouteChange() {
  yield takeEvery('@@router/LOCATION_CHANGE', function* ({ payload, ...rest }) {
    // Added this to remove the hash from the URL after the user logs in with Facebook.
    if (window.location.hash === '#_=_' || window.location.hash === '#') {
      window.location.hash = '';
    }

    const routeId = yield select(getRouteMerchantId);
    const isOnboardingId = !Number(routeId);
    inlineManual.update();

    analytics.pageView(payload.location.pathname);
    if (routeId && routeId !== session.merchant) {
      session.merchant = !isOnboardingId ? Number(routeId) : void 0;
      if (isOnboardingId) {
        yield put(actions.setOnboardingId(routeId));
      } else {
        yield put(actions.setMerchantId(routeId));
      }
    }
  });
}

function* watchForgotPassword() {
  yield takeEvery(success(actions.forgotPassword), function* () {
    yield put(replace('/auth/forgot-password-success'));
  });
}

function* watchResetPassword() {
  yield takeEvery(success(actions.resetPassword), function* () {
    yield put(replace('/auth/sign-in?successRecovery=true'));
  });
  yield takeEvery(error(actions.resetPassword), function* () {
    yield put(replace('/auth/forgot-password?tokenExpired=true'));
  });
}

function* watchCreatePassword() {
  yield takeEvery(success(actions.setPassword), function* ({ payload }) {
    session.token = payload.token;
    yield put(replace('/'));
  });
}

function* watchUpdatePlatformError() {
  yield takeEvery(error(actions.updateMerchantPlatform), function* () {
    yield put(
      notifications.create({
        type: 'warning',
        text: 'An error occurred during the request. Please check your details to make sure they are correct.',
      })
    );
  });
}

export const saga = [
  // ...trackingSagas,
  // watchCC(),
  // watch403errors(),
  watchSignOut(),
  watchKcSignOut(),
  watchUserDataFetchFailure(),
  watchRouteChange(),
  watchMerchantChange(),
  watchForgotPassword(),
  watchCreatePassword(),
  watchResetPassword(),
  watchUserDataFetchSuccess(),
];
